import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, X, EyeOff, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { BusinessPreviewDialog } from "@/components/admin/BusinessPreviewDialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { kindLabel, statusLabel, type BusinessStatus } from "@/lib/admin-labels";

type SearchParams = { status?: FilterTab };
export const Route = createFileRoute("/admin/podniky")({
  component: BusinessesPage,
  validateSearch: (s: Record<string, unknown>): SearchParams => {
    const v = s.status;
    const allowed: FilterTab[] = ["all", "pending", "approved", "rejected", "hidden"];
    return { status: (allowed as string[]).includes(v as string) ? (v as FilterTab) : undefined };
  },
});

type FilterTab = "all" | BusinessStatus;

const statusVariant: Record<BusinessStatus, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "outline", approved: "default", rejected: "destructive", hidden: "secondary",
};

function BusinessesPage() {
  const qc = useQueryClient();
  const initialTab = Route.useSearch().status ?? "all";
  const [tab, setTab] = useState<FilterTab>(initialTab);
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState<{ id: string; name: string; action: "reject" | "hide" } | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const { data: businesses = [], isLoading } = useQuery({
    queryKey: ["admin", "businesses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("id,name,kind,city,country,status,reports_count,submitted_at,owner_id,profiles:owner_id(email,owner_verified)")
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: BusinessStatus }) => {
      const { error } = await supabase.from("businesses").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "businesses"] });
      qc.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setStatus = (id: string, status: BusinessStatus, message: string) =>
    updateMut.mutate({ id, status }, { onSuccess: () => toast.success(message) });

  const filtered = useMemo(() => {
    return businesses.filter((b) => {
      const matchesTab = tab === "all" || b.status === tab;
      const t = q.trim().toLowerCase();
      const ownerEmail = (b.profiles as { email: string | null } | null)?.email ?? "";
      const matchesQ = !t || b.name.toLowerCase().includes(t) || b.city.toLowerCase().includes(t) || ownerEmail.toLowerCase().includes(t);
      return matchesTab && matchesQ;
    });
  }, [businesses, tab, q]);

  const counts = useMemo(() => ({
    all: businesses.length,
    pending: businesses.filter((b) => b.status === "pending").length,
    approved: businesses.filter((b) => b.status === "approved").length,
    rejected: businesses.filter((b) => b.status === "rejected").length,
    hidden: businesses.filter((b) => b.status === "hidden").length,
  }), [businesses]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl">Podniky</h1>
          <p className="text-sm text-muted-foreground">
            Hybridní režim: ověření majitelé publikují ihned, ostatní čekají na schválení.
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat název, město, majitele…" className="pl-8" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as FilterTab)}>
        <TabsList>
          <TabsTrigger value="all">Vše ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending">Čeká ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved">Schválené ({counts.approved})</TabsTrigger>
          <TabsTrigger value="rejected">Zamítnuté ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="hidden">Skryté ({counts.hidden})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Podnik</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Lokalita</TableHead>
                <TableHead>Majitel</TableHead>
                <TableHead>Stav</TableHead>
                <TableHead>Reporty</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">Načítám…</TableCell></TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">Žádné podniky neodpovídají filtru.</TableCell></TableRow>
              )}
              {filtered.map((b) => {
                const owner = b.profiles as { email: string | null; owner_verified: boolean | null } | null;
                return (
                  <TableRow key={b.id}>
                    <TableCell>
                      <div className="font-medium">{b.name}</div>
                      <div className="text-xs text-muted-foreground">Přidáno {new Date(b.submitted_at).toLocaleDateString("cs-CZ")}</div>
                    </TableCell>
                    <TableCell>{kindLabel[b.kind as keyof typeof kindLabel]}</TableCell>
                    <TableCell>{b.city}, {b.country}</TableCell>
                    <TableCell>
                      <div className="text-sm">{owner?.email ?? <span className="text-muted-foreground">—</span>}</div>
                      {owner?.owner_verified && <div className="text-[11px] text-primary">ověřený majitel</div>}
                    </TableCell>
                    <TableCell><Badge variant={statusVariant[b.status as BusinessStatus]}>{statusLabel[b.status as BusinessStatus]}</Badge></TableCell>
                    <TableCell>{b.reports_count > 0 ? <Badge variant="destructive">{b.reports_count}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex flex-wrap justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setPreviewId(b.id)}>
                          <Eye className="mr-1 h-4 w-4" /> Náhled
                        </Button>
                        {b.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => setStatus(b.id, "approved", `Schváleno: ${b.name}`)}>
                              <Check className="mr-1 h-4 w-4" /> Schválit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => setConfirm({ id: b.id, name: b.name, action: "reject" })}>
                              <X className="mr-1 h-4 w-4" /> Zamítnout
                            </Button>
                          </>
                        )}
                        {b.status === "approved" && (
                          <Button size="sm" variant="outline" onClick={() => setConfirm({ id: b.id, name: b.name, action: "hide" })}>
                            <EyeOff className="mr-1 h-4 w-4" /> Skrýt
                          </Button>
                        )}
                        {(b.status === "rejected" || b.status === "hidden") && (
                          <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "approved", `Obnoveno: ${b.name}`)}>
                            <Eye className="mr-1 h-4 w-4" /> Obnovit
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.action === "reject" ? "Zamítnout podnik?" : "Skrýt podnik?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "reject"
                ? "Podnik se nezobrazí v katalogu. Majitele upozorněte ručně."
                : "Podnik bude dočasně skrytý do vyřešení problému."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (!confirm) return;
              setStatus(
                confirm.id,
                confirm.action === "reject" ? "rejected" : "hidden",
                confirm.action === "reject" ? `Zamítnuto: ${confirm.name}` : `Skryto: ${confirm.name}`,
              );
              setConfirm(null);
            }}>Potvrdit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BusinessPreviewDialog
        businessId={previewId}
        open={!!previewId}
        onOpenChange={(o) => !o && setPreviewId(null)}
      />
    </div>
  );
}
